import { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  CardContent,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { ApiEndPoints } from "src/network/endpoints";
import PageHeader from "src/@core/components/page-header";
import { DefaultPaginationSettings } from "src/constants/general.const";
import Translations from "src/layouts/components/Translations";
import { toastError, toastSuccess } from "src/utils/utils";
import { axiosInstance } from "src/network/adapter";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import DialogSubAdmin from "src/views/dialog/DialogSubAdmin";
import TableSubAdmin from "src/views/tables/TableSubAdmin";
import DialogUserStatusUpdate from "src/views/dialog/DialogUserStatusUpdate";

const SubAdminPage = () => {
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [subAdminData, setSubAdminData] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const [subAdminFormDialogOpen, setSubAdminFormDialogOpen] = useState(false);
  const [subAdminFormDialogMode, setSubAdminFormDialogMode] = useState("add");
  const [subAdminToEdit, setSubAdminToEdit] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const toggleSubAdminFormDialog = (e, mode = "add", subAdminToEdit = null) => {
    setSubAdminFormDialogOpen((prev) => !prev);
    setSubAdminFormDialogMode(mode);
    setSubAdminToEdit(subAdminToEdit);
  };

  // Confirmation
  const [confirmationDialogLoading, setConfirmationDialogLoading] =
    useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [subAdminToDelete, setSubAdminToDelete] = useState(null);

  const toggleConfirmationDialog = (e, dataToDelete = null) => {
    setConfirmationDialogOpen((prev) => !prev);
    setSubAdminToDelete(dataToDelete);
  };

  // Function to open status dialog
  const handleStatusDialogOpen = (user) => {
    setSelectedUser(user);
    setStatusDialogOpen(true);
  };

  // Function to close status dialog
  const handleStatusDialogClose = () => {
    setSelectedUser(null);
    setStatusDialogOpen(false);
  };

  const fetchData = ({
    currentPage,
    pageSize = DefaultPaginationSettings.ROWS_PER_PAGE,
    search,
  }) => {
    setLoading(true);
    let params = {
      page: currentPage,
      limit: pageSize,
      search: search,
      status: status,
    };
    axiosInstance
      .get(ApiEndPoints.SUB_ADMIN.list, { params })
      .then((response) => {
        setSubAdminData(response.data.data.subAdmin);
        setTotalCount(response.data.data.totalRecords);
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData({
      currentPage: currentPage,
      pageSize: pageSize,
      search: search,
      status: status,
    });
  }, [currentPage, pageSize, search, status]);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
    }, 500);
  };

  const onConfirmDelete = useCallback(
    (e) => {
      e?.preventDefault();
      setConfirmationDialogLoading(true);
      axiosInstance
        .delete(ApiEndPoints.SUB_ADMIN.delete(subAdminToDelete.id))
        .then((response) => response.data)
        .then((response) => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
          });
          toggleConfirmationDialog();
          toastSuccess(response.message);
        })
        .catch((error) => {
          toastError(error);
        })
        .finally(() => {
          setConfirmationDialogLoading(false);
        });
    },
    [subAdminToDelete, currentPage, pageSize]
  );

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <PageHeader
            title={
              <Typography variant="h5">
                <Translations text="Sub Admin" />
              </Typography>
            }
            action={
              <Button variant="contained" onClick={toggleSubAdminFormDialog}>
                Add Sub Admin
              </Button>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  pb: 5,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "end",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Select
                    size="small"
                    defaultValue={" "}
                    sx={{ bgcolor: "#F7FBFF" }}
                    value={status === "" ? " " : status}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setStatus(selectedValue === " " || selectedValue === "All" ? "" : selectedValue);
                    }}
                  >
                    <MenuItem disabled value={" "}>
                      <em>Status</em>
                    </MenuItem>
                    <MenuItem value={"All"}>All</MenuItem>
                    <MenuItem value={"0"}>Active</MenuItem>
                    <MenuItem value={"1"}>Inactive</MenuItem>
                    <MenuItem value={"2"}>Deleted</MenuItem>
                  </Select>
                  <TextField
                    type="search"
                    size="small"
                    placeholder="Search"
                    onChange={handleSearchChange}
                  />
                </Box>
              </Box>

              <TableSubAdmin
                search={search}
                loading={loading}
                rows={subAdminData}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleEdit={toggleSubAdminFormDialog}
                toggleDelete={toggleConfirmationDialog}
                toggleStatus={handleStatusDialogOpen}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <DialogSubAdmin
        mode={subAdminFormDialogMode}
        open={subAdminFormDialogOpen}
        toggle={toggleSubAdminFormDialog}
        dataToEdit={subAdminToEdit}
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
          });
        }}
      />
      <DialogUserStatusUpdate
        open={statusDialogOpen}
        toggle={handleStatusDialogClose}
        dataToEdit={selectedUser}
        endpointType='sub-admin'
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
      />
      <DialogConfirmation
        loading={confirmationDialogLoading}
        title="Delete Sub Admin"
        subtitle="Are you sure you want to delete this Sub Admin?"
        open={confirmationDialogOpen}
        toggle={toggleConfirmationDialog}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default SubAdminPage;
